import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { rf } from "./theme";
import { Pill, PushButton } from "./ui";
import { formatDate, formatSchedule, money, priceOf } from "./cohortStatus";
import { useCheckout, useGoogleSignIn, getCurrentUser } from "./api/hooks";

// Joining a cohort is three steps and there is no way round the first one:
// Google is the only sign-in, so an account exists before money is discussed.
//
// All three are mocked. Nobody is authenticated, nothing is charged, and no
// dashboard exists yet — the screen says so rather than implying otherwise.
const STEPS = ["Sign in", "Pay", "Start"];

const Stepper = ({ active }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              border: rf.lineThin,
              backgroundColor: i < active ? rf.mint : i === active ? rf.coral : rf.paper,
              fontFamily: rf.display,
              fontWeight: 800,
              fontSize: 12,
              color: rf.ink,
            }}
          >
            {i < active ? "✓" : i + 1}
          </Box>
          <Typography
            sx={{
              fontFamily: rf.display,
              fontSize: 13,
              fontWeight: i === active ? 800 : 600,
              color: i === active ? rf.ink : rf.inkFaint,
            }}
          >
            {label}
          </Typography>
        </Box>
        {i < STEPS.length - 1 && <Box sx={{ flex: 1, height: 2, backgroundColor: i < active ? rf.ink : rf.creamDeep }} />}
      </React.Fragment>
    ))}
  </Box>
);

const Row = ({ label, value, strong }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: strong ? 0 : 0.75 }}>
    <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, fontWeight: strong ? 700 : 500, color: strong ? rf.ink : rf.inkSoft }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, fontWeight: 700, color: rf.ink, textAlign: "right" }}>
      {value}
    </Typography>
  </Box>
);

// Google's own button is rendered by their SDK in the real flow; this is a
// stand-in with the same placement and weight.
const GoogleButton = ({ onClick, busy }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={busy}
    sx={{
      width: "100%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.25,
      px: 3,
      py: 1.4,
      backgroundColor: rf.paper,
      border: rf.line,
      borderRadius: rf.radiusPill,
      boxShadow: rf.shadowSm,
      fontFamily: rf.display,
      fontWeight: 700,
      fontSize: 15.5,
      color: rf.ink,
      cursor: busy ? "wait" : "pointer",
      "&:hover": busy ? {} : { transform: "translate(1px, 1px)", boxShadow: "2px 2px 0 #141414" },
    }}
  >
    {busy ? (
      <CircularProgress size={20} sx={{ color: rf.ink }} />
    ) : (
      <>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }} aria-hidden="true">
          <path fill="#4285F4" d="M23 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.2a5.3 5.3 0 0 1-2.3 3.5v2.9h3.7c2.2-2 3.4-5 3.4-8.6z" />
          <path fill="#34A853" d="M12 24c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.3 1.1-3.9 1.1-3 0-5.5-2-6.4-4.7H1.8v3C3.7 21.4 7.6 24 12 24z" />
          <path fill="#FBBC05" d="M5.6 14.7a7.2 7.2 0 0 1 0-4.6v-3H1.8a12 12 0 0 0 0 10.6l3.8-3z" />
          <path fill="#EA4335" d="M12 4.8c1.7 0 3.2.6 4.4 1.7l3.3-3.3C17.7 1.2 15.1 0 12 0 7.6 0 3.7 2.6 1.8 6.1l3.8 3C6.5 6.7 9 4.8 12 4.8z" />
        </Box>
        Continue with Google
      </>
    )}
  </Box>
);

export const EnrollFlow = ({ cohort, onClose }) => {
  const _p = new URLSearchParams(window.location.search).get("step");
  const [user, setUser] = useState(null);
  const [result, setResult] = useState(_p === "done" ? { enrollment: { id: "ENR-MOCK-48211" } } : null);

  const signIn = useGoogleSignIn();
  const checkout = useCheckout();

  // Check for existing auth on mount
  useEffect(() => {
    const existingUser = getCurrentUser();
    if (existingUser?.accessToken) {
      setUser(existingUser);
    }
  }, []);

  const price = priceOf(cohort);
  const step = result ? 2 : user ? 1 : 0;

  const handleSignIn = async () => {
    try {
      setUser(await signIn.mutateAsync());
    } catch {
      /* the error state below renders */
    }
  };

  const handlePay = async () => {
    try {
      setResult(await checkout.mutateAsync({ cohortSlug: cohort.slug, cohortTitle: cohort.title, user }));
    } catch {
      /* the error state below renders */
    }
  };

  const failed = signIn.isError || checkout.isError;

  return (
    <Box>
      <Stepper active={step} />

      {step === 0 && (
        <>
          <Typography sx={{ fontFamily: rf.body, fontSize: 15, color: rf.inkSoft, lineHeight: 1.6, mb: 2.5 }}>
            Joining starts with a Google account — it is the only sign-in we use, and it is what your classes,
            assignments and receipts will be attached to.
          </Typography>
          <GoogleButton onClick={handleSignIn} busy={signIn.isPending} />
        </>
      )}

      {step === 1 && (
        <>
          <Box sx={{ p: 2, mb: 2.5, backgroundColor: rf.mint, border: rf.lineThin, borderRadius: rf.radiusSm }}>
            <Typography sx={{ fontFamily: rf.body, fontSize: 14, fontWeight: 700, color: rf.ink }}>
              Signed in as {user.name} · {user.email}
            </Typography>
          </Box>

          <Box sx={{ p: 2.5, mb: 2.5, backgroundColor: rf.paper, border: rf.lineThin, borderRadius: rf.radiusSm }}>
            <Row label="Cohort" value={cohort.title} />
            <Row label="Starts" value={formatDate(cohort.start_date)} />
            <Row label="Live sessions" value={formatSchedule(cohort)} />
            <Box sx={{ pt: 1.25, mt: 1.25, borderTop: rf.lineThin }}>
              <Row label="Payable now" value={money(price.payable, price.currency)} strong />
            </Box>
          </Box>

          <PushButton variant="primary" size="lg" fullWidth onClick={handlePay} disabled={checkout.isPending}>
            {checkout.isPending ? <CircularProgress size={22} sx={{ color: rf.ink }} /> : `Pay ${money(price.payable, price.currency)}`}
          </PushButton>
        </>
      )}

      {step === 2 && (
        <>
          <Box sx={{ p: 2.5, mb: 2.5, backgroundColor: rf.mint, border: rf.line, borderRadius: rf.radiusSm, boxShadow: rf.shadowSm }}>
            <Typography sx={{ fontFamily: rf.display, fontSize: 17, fontWeight: 800, color: rf.ink, mb: 0.5 }}>
              You're in — {result.enrollment.id}
            </Typography>
            <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, color: rf.ink, lineHeight: 1.6 }}>
              Joining instructions and the calendar invites would arrive by email, and your dashboard would open the
              week before {formatDate(cohort.start_date)}.
            </Typography>
          </Box>
          <PushButton variant="dark" onClick={onClose}>
            Done
          </PushButton>
        </>
      )}

      {failed && (
        <Box sx={{ p: 2, mt: 2, backgroundColor: rf.coral, border: rf.lineThin, borderRadius: rf.radiusSm }}>
          <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, fontWeight: 600, color: rf.ink }}>
            That didn't go through. Try again in a moment.
          </Typography>
        </Box>
      )}

      <Pill bg={rf.amber} sx={{ mt: 2.5, fontSize: 11.5 }}>
        Demo — no account created, no payment taken
      </Pill>
    </Box>
  );
};

export default EnrollFlow;
